import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

export default function TaskHistory({ taskId, showHistory, setShowHistory }) {

    const [taskHistory, setTaskHistory] = useState([])
    const dispatch = useDispatch()

    useEffect(() => {
        const fetchData = async () => {
            const getTaskHistory = await dispatch.projectDetails.getTaskHistory({ taskId })
            if (getTaskHistory) {
                const historyData = getTaskHistory.result || getTaskHistory.data || getTaskHistory.history || getTaskHistory;
                setTaskHistory(Array.isArray(historyData) ? historyData : []);
            }
        }
        fetchData()
    }, [taskId])

    const parseValueObj = (val) => {
        if (!val) return {};
        if (typeof val === 'object') return val;
        if (typeof val === 'string') {
            const trimmed = val.trim();
            if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
                try {
                    const parsed = JSON.parse(trimmed);
                    if (parsed && typeof parsed === 'object') return parsed;
                } catch (e) {}
            }
        }
        return {};
    };

    const getGroupVal = (val, valDetails) => {
        if (valDetails && valDetails.group_id) {
            const detailVal = valDetails.group_id;
            if (typeof detailVal === "object" && detailVal !== null) {
                if (detailVal.group_name || detailVal.name || detailVal.title) {
                    return String(detailVal.group_name || detailVal.name || detailVal.title).trim();
                }
            }
        }
        if (val === undefined || val === null) return null;
        if (typeof val === "object" && val !== null) {
            const extracted = val.group_name || val.name || val.title || val.group_id || val.group;
            if (extracted === undefined || extracted === null) return null;
            return getGroupVal(extracted);
        }
        const str = String(val).trim();
        if (str === "" || str.toLowerCase() === "null" || str.toLowerCase() === "undefined") return null;
        return str.replace(/"/g, "");
    };

    const getAssigneeName = (val, valDetails) => {
        const target = (valDetails && (valDetails.assigned_to || valDetails.assignee)) || val;
        if (!target) return "";
        if (Array.isArray(target)) {
            return target
                .map((item) => {
                    if (typeof item === "object" && item !== null) {
                        return item.full_name || `${item.first_name || ""} ${item.last_name || ""}`.trim() || item.name || item.email || "";
                    }
                    return String(item);
                })
                .filter(Boolean)
                .join(", ")
                .replace(/"/g, "");
        }
        if (typeof target === "object" && target !== null) {
            return (target.full_name || `${target.first_name || ""} ${target.last_name || ""}`.trim() || target.name || target.email || "").replace(/"/g, "");
        }
        return String(target).replace(/"/g, "");
    };

    const formatDateStr = (val) => {
        if (!val) return "";
        const d = new Date(val);
        if (!isNaN(d.getTime())) {
            return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        }
        return String(val).replace(/"/g, "");
    };

    const formatHistoryText = (item) => {
        if (!item) return "";
        const prevValObj = parseValueObj(item.previous_value);
        const newValObj = parseValueObj(item.new_value);
        const eventType = String(item.event_type || item.action_type || item.type || "").toUpperCase();

        // 1. Description Change
        if (eventType === "DESCRIPTION_CHANGE" || eventType === "TASK_DESCRIPTION_CHANGE" || "task_description" in prevValObj || "task_description" in newValObj || "description" in prevValObj || "description" in newValObj) {
            return "Description has been changed";
        }

        // 2. Assignment Change
        if (eventType === "ASSIGNMENT_CHANGE" || eventType === "ASSIGNEE_CHANGE" || "assigned_to" in prevValObj || "assigned_to" in newValObj || "assignee" in prevValObj || "assignee" in newValObj) {
            const prevAssignee = getAssigneeName(prevValObj.assigned_to || prevValObj.assignee, item.previous_value_details);
            const newAssignee = getAssigneeName(newValObj.assigned_to || newValObj.assignee, item.new_value_details);

            if (prevAssignee && newAssignee && prevAssignee !== newAssignee) {
                return `Assignee changed from ${prevAssignee} to ${newAssignee}`;
            } else if (newAssignee) {
                return `Assigned to ${newAssignee}`;
            } else if (prevAssignee) {
                return `Unassigned from ${prevAssignee}`;
            }
        }

        // 3. Due Date Change
        const isDueDateChange =
            eventType === "DUE_DATE_CHANGE" ||
            eventType === "DUE_DATE_UPDATED" ||
            eventType === "TASK_DUE_DATE_CHANGE" ||
            eventType === "DUE_DATE" ||
            eventType.includes("DUE_DATE") ||
            eventType.includes("END_DATE") ||
            "task_end_date" in prevValObj ||
            "task_end_date" in newValObj ||
            "due_date" in prevValObj ||
            "due_date" in newValObj;

        if (isDueDateChange) {
            const prevDate = formatDateStr(prevValObj.task_end_date || prevValObj.due_date || (typeof item.previous_value !== 'object' && !String(item.previous_value).startsWith('{') ? item.previous_value : ''));
            const newDate = formatDateStr(newValObj.task_end_date || newValObj.due_date || (typeof item.new_value !== 'object' && !String(item.new_value).startsWith('{') ? item.new_value : ''));

            if (prevDate && newDate && prevDate !== newDate) {
                return `Due date changed from ${prevDate} to ${newDate}`;
            } else if (newDate) {
                return `Due date set to ${newDate}`;
            }
        }

        // 4. Group Change
        const isGroupChange =
            eventType.includes("GROUP") ||
            "group_id" in prevValObj ||
            "group_id" in newValObj ||
            "group_name" in prevValObj ||
            "group_name" in newValObj ||
            "group" in prevValObj ||
            "group" in newValObj;

        if (isGroupChange) {
            const prevGroup = getGroupVal(prevValObj.group_id ?? prevValObj.group_name ?? prevValObj.group ?? (typeof item.previous_value !== 'object' && !String(item.previous_value).startsWith('{') ? item.previous_value : null), item.previous_value_details);
            const newGroup = getGroupVal(newValObj.group_id ?? newValObj.group_name ?? newValObj.group ?? (typeof item.new_value !== 'object' && !String(item.new_value).startsWith('{') ? item.new_value : null), item.new_value_details);

            if (!prevGroup && newGroup) {
                return `Moved to group ${newGroup}`;
            } else if (prevGroup && newGroup && prevGroup !== newGroup) {
                return `Moved from group ${prevGroup} to ${newGroup}`;
            } else if (prevGroup && !newGroup) {
                return `Removed from group ${prevGroup}`;
            } else if (newGroup) {
                return `Moved to group ${newGroup}`;
            }
        }

        // 5. Comment Added
        if (eventType.includes("COMMENT") || item.comment || item.message || newValObj.comment_text || newValObj.comment) {
            const commentTxt = item.comment || item.message || item.text || item.content || newValObj.comment_text || newValObj.comment || "";
            const cleanComment = String(commentTxt).replace(/"/g, "");
            return cleanComment ? `Added a comment: ${cleanComment}` : "Added a comment";
        }

        // 6. Create
        if (eventType === "CREATE" || eventType === "WORK_CREATE" || eventType === "TASK_CREATE") {
            return "Task was created";
        }

        // 7. Status Change
        const prevStatus = (prevValObj.status_name || prevValObj.task_status?.status_name || item.previous_value_details?.task_status?.status_name || "").replace(/"/g, "");
        const newStatus = (newValObj.status_name || newValObj.task_status?.status_name || item.new_value_details?.task_status?.status_name || "").replace(/"/g, "");
        if (eventType === "STATUS_CHANGE" || prevStatus || newStatus || "status_name" in prevValObj || "status_name" in newValObj) {
            if (prevStatus && newStatus && prevStatus !== newStatus) {
                return `Status changed from ${prevStatus} to ${newStatus}`;
            } else if (newStatus) {
                return `Status updated to ${newStatus}`;
            }
        }

        // 8. Priority Change
        const prevPrio = (prevValObj.task_priority || prevValObj.priority || "").replace(/"/g, "");
        const newPrio = (newValObj.task_priority || newValObj.priority || "").replace(/"/g, "");
        if (eventType === "PRIORITY_CHANGE" || prevPrio || newPrio || "task_priority" in prevValObj || "task_priority" in newValObj) {
            if (prevPrio && newPrio && prevPrio !== newPrio) {
                return `Priority changed from ${prevPrio} to ${newPrio}`;
            } else if (newPrio) {
                return `Priority updated to ${newPrio}`;
            }
        }

        if (item.action || item.description || item.message) {
            return String(item.action || item.description || item.message).replace(/\s*(?:→|->|=>)\s*/g, " - ").replace(/"/g, "");
        }

        if (item.previous_value && item.new_value) {
            const formatVal = (val) => typeof val === 'object' ? (val.name || val.title || JSON.stringify(val)) : String(val);
            return `Updated from ${formatVal(item.previous_value)} to ${formatVal(item.new_value)}`.replace(/"/g, "");
        }

        const formattedType = eventType ? eventType.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : "Update";
        return `${formattedType} made to task`.replace(/"/g, "");
    };

    return (
        <div
            className={`fixed bottom-0 flex flex-col max-w-full bg-white  bg-clip-padding shadow-sm outline-none transition duration-300 ease-in-out text-gray-700 top-0 right-0 border-none w-96 z-50`}>
            <div className="flex items-center justify-between p-4 pl-6">
                <h5 className="mb-0 leading-normal font-semibold">
                    Task History
                </h5>
                <button type="button"
                    className="btn-close box-content w-4 h-4 p-2 -my-5 -mr-2 text-black border-none rounded-none opacity-50 focus:shadow-none focus:outline-none focus:opacity-100 hover:text-black hover:opacity-75 hover:no-underline"
                    onClick={() => setShowHistory(false)}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <hr className="w-full" />

            <div className="container mx-auto w-full h-full mb-4">
                <div className="p-4 pl-6 overflow-y-auto">
                    <label className="text-sm font-semibold">Previous Tasks</label>
                </div>
                {
                    taskHistory.length !== 0 ?
                        <div className="border-2-2 absolute ml-10 border-dashed pt-2 border-blue-500 h-full border"></div>
                        : null
                }
                <div className="relative wrap overflow-y-auto pl-10 pt-4 pr-10 h-full">
                    <div className="w-full mb-24">
                        {
                            taskHistory.length === 0 ?
                                <p className='text-center'>No Task History</p>
                                :
                                taskHistory.map((item, index) => {
                                    const dateStr = item.createdAt || item.created_at || item.updatedAt || item.date;
                                    const formattedDate = dateStr ? moment(dateStr).format('YYYY-MM-DD HH:mm') : '';

                                    if (Array.isArray(item.updated_fields) && item.updated_fields.length > 0) {
                                        return item.updated_fields.map((field, fieldIndex) => {
                                            const key = Object.keys(field)[0];
                                            return (
                                                <div key={`${index}-${fieldIndex}`} className='flex'>
                                                    <div className="z-20 pl-1">
                                                        <div className="my-4 rounded-full h-10 w-10 flex items-center bg-indigo-300 ring-4 ring-indigo-400 ring-opacity-30">
                                                        </div>
                                                    </div>
                                                    <div className="bg-gray-300 mb-10 rounded-lg shadow-xl ml-2 w-full text-sm px-6 py-4">
                                                        <p className="text-sm leading-snug tracking-wide text-gray-900 text-opacity-100 font-medium">
                                                            {key} was changed on {moment(dateStr || Date.now()).format('YYYY-MM-DD')}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        });
                                    }

                                    const historyMessage = formatHistoryText(item);

                                    return (
                                        <div key={index} className='flex'>
                                            <div className="z-20 pl-1">
                                                <div className="my-4 rounded-full h-10 w-10 flex items-center bg-indigo-300 ring-4 ring-indigo-400 ring-opacity-30">
                                                </div>
                                            </div>
                                            <div className="bg-gray-300 mb-10 rounded-lg shadow-xl ml-2 w-full text-sm px-6 py-4">
                                                <p className="text-sm leading-snug tracking-wide text-gray-900 text-opacity-100 font-medium">
                                                    {historyMessage}
                                                </p>
                                                {formattedDate && (
                                                    <span className="text-xs text-gray-600 block mt-1">
                                                        {formattedDate}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

